import type {
  StickerCollectionCodeFormat,
  StickerCollectionStatus,
} from '@/data/stickerCollection.types';

import { slugifyTitle } from '@/utils/slug';

export { slugifyTitle };

/** Slug from `text`, or `<prefix>-<random>` when nothing URL-safe is left (e.g. "日本語"). */
export function ensureSlug(text: string, fallbackPrefix: string): string {
  return slugifyTitle(text) || `${fallbackPrefix}-${Math.random().toString(36).slice(2, 8)}`;
}

export function collectionStatusLabel(status: StickerCollectionStatus | string): string {
  switch (status) {
    case 'PUBLISHED':
      return 'Published';
    case 'ARCHIVED':
      return 'Archived';
    case 'DRAFT':
    default:
      return 'Draft';
  }
}

export function collectionStatusBadgeClass(status: StickerCollectionStatus | string): string {
  switch (status) {
    case 'PUBLISHED':
      return 'status-badge--published';
    case 'ARCHIVED':
      return 'status-badge--archived';
    case 'DRAFT':
    default:
      return 'status-badge--draft';
  }
}

export type DraftStickerMode = 'new' | 'existing';

export interface DraftCode {
  code: string;
  maxUses: number | null;
}

/** A sticker card added in the CMS but not saved yet; saved with "Save Collection". */
export interface DraftSticker {
  key: string;
  mode: DraftStickerMode;
  name: string;
  imageUrl: string;
  thumbnailUrl: string;
  stickerId: string;
  /** Set once a "new" sticker exists, so a retry after a later failure reuses it. */
  createdStickerId?: string;
  clue: string;
  requiresCode: boolean;
  codes: DraftCode[];
}

export function newDraftSticker(): DraftSticker {
  return {
    key: Math.random().toString(36).slice(2, 10),
    mode: 'new',
    name: '',
    imageUrl: '',
    thumbnailUrl: '',
    stickerId: '',
    clue: '',
    requiresCode: true,
    codes: [],
  };
}

export const CODE_FORMAT_OPTIONS: { value: StickerCollectionCodeFormat; label: string }[] = [
  { value: 'ALPHANUMERIC', label: 'Alphanumeric (8 chars)' },
  { value: 'NUMERIC', label: 'Numeric only (6 digits)' },
  { value: 'CUSTOM', label: 'Custom format' },
];

/** Backend range for "stickers needed to complete". */
export const MAX_REQUIRED_STICKERS = 50;

const CODE_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
const DIGITS = '0123456789';

/**
 * Code in the same shape the backend generates for `format`: 8 letters/digits, or 6 digits.
 * CUSTOM collections have no generated codes, so this returns ''.
 */
export function generateCollectionCode(
  format: StickerCollectionCodeFormat = 'ALPHANUMERIC',
): string {
  if (format === 'CUSTOM') return '';
  const [chars, length] = format === 'NUMERIC' ? [DIGITS, 6] : [CODE_CHARS, 8];
  const bytes = crypto.getRandomValues(new Uint8Array(length));
  // 256 is not a multiple of the alphabet size, so the first chars are very slightly likelier;
  // fine for unlock codes.
  return Array.from(bytes, (b) => chars[b % chars.length]).join('');
}

export function codeInputPlaceholder(format: StickerCollectionCodeFormat): string {
  return format === 'CUSTOM' ? 'Type a code (custom format)' : 'Enter code or generate automatically';
}

export function parseMaxUses(value: string): number | null {
  const n = parseInt(value, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export interface CodeEligibleSticker {
  cost?: number | null;
  campaignType?: string | null;
  entityId?: string | null;
}

/**
 * Why unlock codes can't be required on this sticker ('' when they can). Mirrors the backend:
 * only free stickers (donation "Campaign" ones count as paid) made by the collection's owner.
 */
export function codeRequirementBlockedReason(
  sticker: CodeEligibleSticker | null | undefined,
  collectionOwnerId: string,
): string {
  if (!sticker) return '';
  if ((sticker.cost ?? 0) > 0 || sticker.campaignType === 'Campaign') {
    return 'Codes can only be required on free stickers';
  }
  if (collectionOwnerId && sticker.entityId && sticker.entityId !== collectionOwnerId) {
    return "Codes can only be required on stickers the collection's owner created";
  }
  return '';
}
